import { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";

import { Add } from "@mui/icons-material";
import { Box } from "@mui/material";

import * as BotpressSessionActions from "../../state/redux/botpressSession.js";
import AlertDialog from "../dialogs/alertDialog.js";
import BotpressMemberVerifiedAnswersDialog from "../dialogs/botpressMemberVerifiedAnswersDialog.js";
import BotpressMemberVerifiedAnswersTable from "../ui/botpressMemberVerifiedAnswersTable.js";
import OmniResponsiveButton from "../ui/omniResponsiveButton.js";
import Widget from "../ui/widget.js";
import WidgetSkeleton from "../ui/widgetSkeleton.js";
import WidgetSurveyContent from "../ui/widgetSurveyContent.js";
import WidgetTitle from "../ui/widgetTitle.js";

const FAQ_KNOWLEDGE_BASE_ID = "kb-default";
const FAQ_MARKDOWN_FILE_NAME = "FAQ.md";
const FAQ_MARKDOWN_FILE_KEY = `${FAQ_KNOWLEDGE_BASE_ID}/${FAQ_MARKDOWN_FILE_NAME}`;
const FAQ_KNOWLEDGE_BASE_TAGS = {
  source: "knowledge-base",
  kbId: FAQ_KNOWLEDGE_BASE_ID,
  title: "FAQ",
};

const LEGACY_FAQ_KNOWLEDGE_BASE_ID = "kb-faq";
const LEGACY_FAQ_MARKDOWN_FILE_KEY = `${LEGACY_FAQ_KNOWLEDGE_BASE_ID}/${FAQ_MARKDOWN_FILE_NAME}`;

// Stable empty-array reference. Using an inline `[]` as a default prop / redux
// fallback creates a new array on every render, which would invalidate the
// `filesToParse` / `parsedRows` memos every render and drive an infinite
// re-render loop through the effect below.
const EMPTY_FILES = [];

const getMarkdownFileName = file => {
  return (
    file?.name || file?.fileName || file?.filename || file?.path || file?.title || "Unknown File"
  );
};

const getPassageContent = passage => {
  return (
    passage?.content ||
    passage?.text ||
    passage?.value ||
    passage?.chunk ||
    passage?.pageContent ||
    passage?.data?.content ||
    passage?.data?.text ||
    ""
  );
};

const getMarkdownFileContent = file => {
  const directContent =
    file?.content || file?.text || file?.markdown || file?.body || file?.value || "";

  if (directContent) {
    return directContent;
  }

  if (Array.isArray(file?.passages)) {
    return file.passages.map(getPassageContent).filter(Boolean).join("\n");
  }

  return "";
};

const getMarkdownFileKey = file => {
  return file?.key || file?.fileName || file?.filename || file?.name || file?.path || file?.title;
};

const isMarkdownDotMdFile = file => {
  const fileIdentifiers = [file?.key, file?.fileName, file?.filename, file?.name, file?.path]
    .filter(Boolean)
    .map(identifier => String(identifier).trim().split("?")[0].split("#")[0].toLowerCase());

  return fileIdentifiers.some(identifier => identifier.endsWith(".md"));
};

const getFileBaseName = value => {
  const normalized = String(value || "")
    .trim()
    .split("?")[0]
    .split("#")[0];
  const segments = normalized.split("/");

  return segments[segments.length - 1] || normalized;
};

const isFaqFile = file => {
  if (file == null) {
    return false;
  }

  const fileKey = getMarkdownFileKey(file);
  const fileName = getMarkdownFileName(file);

  if (
    fileKey === FAQ_MARKDOWN_FILE_KEY ||
    fileName === FAQ_MARKDOWN_FILE_KEY ||
    fileKey === LEGACY_FAQ_MARKDOWN_FILE_KEY ||
    fileName === LEGACY_FAQ_MARKDOWN_FILE_KEY
  ) {
    return true;
  }

  const baseNameMatches =
    getFileBaseName(fileKey) === FAQ_MARKDOWN_FILE_NAME ||
    getFileBaseName(fileName) === FAQ_MARKDOWN_FILE_NAME;

  const kbId = file?.tags?.kbId || "";

  return (
    baseNameMatches &&
    (kbId === FAQ_KNOWLEDGE_BASE_ID || kbId === LEGACY_FAQ_KNOWLEDGE_BASE_ID)
  );
};

const getMarkdownLineValue = line => {
  return line
    .replace(/^[-*]\s*/, "")
    .replace(/^\d+\.\s*/, "")
    .trim();
};

const buildMarkdownEntryLine = clarification => {
  return `- ${clarification || ""}`;
};

const buildMarkdownEntryBlock = entry => {
  const topic = entry.topic?.trim() || "General";
  const clarification = entry.clarification?.trim() || "";
  const createdAt = entry.createdAt || new Date().toISOString();

  return `[createdAt]: ${createdAt}\n# ${topic}\n- ${clarification}\n\n`;
};

const isCreatedAtLine = line => {
  const trimmedLine = line?.trim() || "";

  return (
    trimmedLine.match(/^\[createdAt\]:\s*(.*?)$/) ||
    trimmedLine.match(/^<!--\s*createdAt:\s*(.*?)\s*-->$/)
  );
};

const removeOrphanHeaders = markdownContent => {
  const lines = markdownContent.split("\n");
  const cleanedLines = [];

  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    const nextNonEmptyLine = lines.slice(index + 1).find(nextLine => nextLine.trim());

    if (isCreatedAtLine(trimmedLine)) {
      const nextLineAfterCreatedAt = lines.slice(index + 1).find(nextLine => nextLine.trim());

      if (!nextLineAfterCreatedAt?.trim().startsWith("#")) {
        return;
      }
    }

    if (trimmedLine.startsWith("#") && !nextNonEmptyLine?.trim().startsWith("-")) {
      return;
    }

    cleanedLines.push(line);
  });

  return cleanedLines.join("\n").trim();
};

const removeMarkdownEntryBlock = (markdownContent, targetLineIndex) => {
  const lines = markdownContent.split("\n");
  const indexesToRemove = new Set([targetLineIndex]);

  for (let index = targetLineIndex - 1; index >= 0; index -= 1) {
    const trimmedLine = lines[index]?.trim();

    if (!trimmedLine) {
      continue;
    }

    if (trimmedLine.startsWith("#")) {
      indexesToRemove.add(index);

      const possibleCreatedAtIndex = index - 1;

      if (isCreatedAtLine(lines[possibleCreatedAtIndex])) {
        indexesToRemove.add(possibleCreatedAtIndex);
      }

      break;
    }
  }

  return lines
    .filter((line, index) => !indexesToRemove.has(index))
    .join("\n")
    .trim();
};

const isSafeFaqFile = fileKey => {
  if (!fileKey) {
    return true;
  }

  return fileKey === FAQ_MARKDOWN_FILE_KEY || getFileBaseName(fileKey) === FAQ_MARKDOWN_FILE_NAME;
};

const parseMarkdownToRows = markdownFiles => {
  if (!Array.isArray(markdownFiles)) {
    return [];
  }

  return markdownFiles.flatMap(file => {
    if (!isMarkdownDotMdFile(file)) {
      return [];
    }

    const fileName = getMarkdownFileName(file);
    const fileKey = getMarkdownFileKey(file);
    const markdownContent = getMarkdownFileContent(file);

    if (!markdownContent) {
      return [];
    }

    const lines = markdownContent.split("\n");
    const rows = [];

    let currentHeader = "General";
    let pendingCreatedAt = "";
    const fileCreatedAt = file?.createdAt || file?.created || file?.lastModified || "";

    lines.forEach((line, index) => {
      const trimmedLine = line.trim();

      if (!trimmedLine) {
        return;
      }

      const createdAtMatch =
        trimmedLine.match(/^\[createdAt\]:\s*(.*?)$/) ||
        trimmedLine.match(/^<!--\s*createdAt:\s*(.*?)\s*-->$/);

      if (createdAtMatch) {
        pendingCreatedAt = createdAtMatch[1];
        return;
      }

      if (trimmedLine.startsWith("#")) {
        currentHeader = trimmedLine.replace(/^#+\s*/, "").trim() || "General";
        return;
      }

      const cleanedValue = getMarkdownLineValue(trimmedLine);

      if (!cleanedValue) {
        return;
      }

      rows.push({
        id: `${file?.id || fileName}-${index}`,
        topic: currentHeader,
        clarification: cleanedValue,
        sourceFile: fileName,
        lineNumber: index + 1,
        lineIndex: index,
        fileId: file?.id,
        fileKey,
        fileContent: markdownContent,
        fileTags: file?.tags || {},
        fileMetadata: file?.metadata || {},
        isSafeFaqFile: isSafeFaqFile(fileKey) || isSafeFaqFile(fileName),
        createdAt: pendingCreatedAt || fileCreatedAt,
      });

      pendingCreatedAt = "";
    });

    return rows;
  });
};

const BotpressMemberVerifiedAnswers = ({
  markdownFiles = EMPTY_FILES,
  knowledgeBaseFiles = EMPTY_FILES,
  knowledgeBaseMarkdownFilesLoading = false,
  widgetSize = "medium",
  minHeight = "333px",
  readOnly = false,
  onRefreshTable = null,
  onUpdateMarkdownFile = null,
}) => {
  const filesToParse = useMemo(() => {
    const sourceFiles =
      Array.isArray(markdownFiles) && markdownFiles.length > 0 ? markdownFiles : knowledgeBaseFiles;

    if (!Array.isArray(sourceFiles)) {
      return [];
    }

    return sourceFiles.filter(isMarkdownDotMdFile);
  }, [knowledgeBaseFiles, markdownFiles]);

  const currentFaqFile = useMemo(
    () =>
      filesToParse.find(
        file => isFaqFile(file) && getMarkdownFileKey(file) === FAQ_MARKDOWN_FILE_KEY,
      ) || null,
    [filesToParse],
  );

  const legacyFaqFile = useMemo(
    () =>
      filesToParse.find(
        file => isFaqFile(file) && getMarkdownFileKey(file) === LEGACY_FAQ_MARKDOWN_FILE_KEY,
      ) || null,
    [filesToParse],
  );

  const manualFile = useMemo(() => {
    if (currentFaqFile && getMarkdownFileContent(currentFaqFile)) {
      return currentFaqFile;
    }
    if (legacyFaqFile && getMarkdownFileContent(legacyFaqFile)) {
      return legacyFaqFile;
    }
    return currentFaqFile || legacyFaqFile || null;
  }, [currentFaqFile, legacyFaqFile]);

  const parsedRows = useMemo(() => {
    return parseMarkdownToRows(manualFile ? [manualFile] : []);
  }, [manualFile]);

  const [rows, setRows] = useState([]);
  const [optimisticRows, setOptimisticRows] = useState([]);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [selectedDeleteEntry, setSelectedDeleteEntry] = useState(null);
  const [isWaitingForBotpressRefresh, setIsWaitingForBotpressRefresh] = useState(false);

  useEffect(() => {
    // While Botpress is re-indexing after create/edit/delete,
    // keep showing the optimistic local rows instead of trusting stale Botpress rows.
    if (isWaitingForBotpressRefresh) {
      setRows(optimisticRows);
      return;
    }

    if (parsedRows.length > 0) {
      setRows(parsedRows);
      // Only clear when there is something to clear. `optimisticRows` is a
      // dependency of this effect, so unconditionally setting a fresh `[]`
      // would retrigger the effect on every render — an infinite re-render loop.
      if (optimisticRows.length > 0) {
        setOptimisticRows([]);
      }
      return;
    }

    setRows([]);
  }, [isWaitingForBotpressRefresh, optimisticRows, parsedRows]);

  useEffect(() => {
    if (!isWaitingForBotpressRefresh) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setIsWaitingForBotpressRefresh(false);
      setOptimisticRows([]);
    }, 8000);

    return () => {
      clearTimeout(timeout);
    };
  }, [isWaitingForBotpressRefresh]);

  const getManualMemberVerifiedAnswersFile = () => manualFile;

  const hasCompletedInitialLoadRef = useRef(false);
  const previousLoadingRef = useRef(knowledgeBaseMarkdownFilesLoading);
  const hasRequestedFileCreationRef = useRef(false);

  useEffect(() => {
    if (previousLoadingRef.current && !knowledgeBaseMarkdownFilesLoading) {
      hasCompletedInitialLoadRef.current = true;
    }

    previousLoadingRef.current = knowledgeBaseMarkdownFilesLoading;
  }, [knowledgeBaseMarkdownFilesLoading]);

  useEffect(() => {
    if (readOnly || !onUpdateMarkdownFile) {
      return;
    }

    if (
      !hasCompletedInitialLoadRef.current ||
      knowledgeBaseMarkdownFilesLoading ||
      isWaitingForBotpressRefresh
    ) {
      return;
    }

    const currentContent = currentFaqFile ? getMarkdownFileContent(currentFaqFile) : "";
    const legacyContent = legacyFaqFile ? getMarkdownFileContent(legacyFaqFile) : "";

    if (currentContent) {
      hasRequestedFileCreationRef.current = false;
      return;
    }

    if (hasRequestedFileCreationRef.current) {
      return;
    }

    if (legacyContent) {
      hasRequestedFileCreationRef.current = true;
      onUpdateMarkdownFile({
        key: FAQ_MARKDOWN_FILE_KEY,
        content: legacyContent,
        index: true,
        tags: FAQ_KNOWLEDGE_BASE_TAGS,
      });
      return;
    }

    if (!currentFaqFile && !legacyFaqFile) {
      hasRequestedFileCreationRef.current = true;
      onUpdateMarkdownFile({
        key: FAQ_MARKDOWN_FILE_KEY,
        content: "",
        index: true,
        tags: FAQ_KNOWLEDGE_BASE_TAGS,
      });
    }
  }, [
    readOnly,
    onUpdateMarkdownFile,
    knowledgeBaseMarkdownFilesLoading,
    isWaitingForBotpressRefresh,
    currentFaqFile,
    legacyFaqFile,
  ]);

  const onCreateMemberVerifiedAnswer = () => {
    const newEntry = {
      id: null,
      topic: "",
      clarification: "",
      sourceFile: FAQ_MARKDOWN_FILE_KEY,
      lineNumber: "",
      createdAt: new Date().toISOString(),
    };

    setSelectedEntry(newEntry);
  };

  const onEditMemberVerifiedAnswer = entry => {
    const targetKey = entry.fileKey || entry.sourceFile;

    if (!isSafeFaqFile(targetKey)) {
      console.warn("Blocked edit for non-test KB file", targetKey);
      return;
    }

    setSelectedEntry(entry);
  };

  const onAcceptMemberVerifiedAnswer = entry => {
    const isNewEntry = selectedEntry?.id == null;

    if (!onUpdateMarkdownFile) {
      setSelectedEntry(null);
      return;
    }

    if (isNewEntry) {
      const existingFile = getManualMemberVerifiedAnswersFile();

      const keyToSave = FAQ_MARKDOWN_FILE_KEY;

      const manualFileName = existingFile
        ? getMarkdownFileName(existingFile)
        : FAQ_MARKDOWN_FILE_KEY;

      const savedEntry = {
        ...entry,
        createdAt: entry.createdAt || new Date().toISOString(),
      };

      const oldContent = existingFile
        ? removeOrphanHeaders(getMarkdownFileContent(existingFile))
        : "";
      const newContent = `${buildMarkdownEntryBlock(savedEntry)}${oldContent ? oldContent : ""}`;

      const pendingRows = [
        {
          id: `pending-entry-${Date.now()}`,
          topic: savedEntry.topic,
          clarification: savedEntry.clarification,
          sourceFile: manualFileName,
          lineNumber: "",
          fileKey: keyToSave,
          fileContent: newContent,
          fileTags: existingFile?.tags || {},
          fileMetadata: existingFile?.metadata || {},
          isSafeFaqFile: true,
          createdAt: savedEntry.createdAt,
        },
        ...rows,
      ];

      setRows(pendingRows);
      setOptimisticRows(pendingRows);
      setIsWaitingForBotpressRefresh(true);

      onUpdateMarkdownFile({
        key: keyToSave,
        content: newContent,
        tags: { ...(existingFile?.tags || {}), ...FAQ_KNOWLEDGE_BASE_TAGS },
        metadata: existingFile?.metadata || {},
        index: true,
      });

      setSelectedEntry(null);
      return;
    }

    const keyToSave = FAQ_MARKDOWN_FILE_KEY;

    const oldContent = selectedEntry.fileContent || "";
    const lines = oldContent.split("\n");
    const oldTopic = selectedEntry?.topic || entry.topic;

    if (entry.topic !== oldTopic) {
      const newContentWithoutOldLine = removeMarkdownEntryBlock(
        oldContent,
        selectedEntry.lineIndex,
      );

      const savedEntry = {
        ...entry,
        createdAt: entry.createdAt || selectedEntry.createdAt || new Date().toISOString(),
      };

      const newContent = `${buildMarkdownEntryBlock(savedEntry)}${newContentWithoutOldLine}`;

      console.log("Updating FAQ.md test file", {
        key: keyToSave,
        content: newContent,
      });

      const updatedRows = rows.map(row =>
        row.id === selectedEntry.id
          ? {
              ...row,
              topic: savedEntry.topic,
              clarification: savedEntry.clarification,
              createdAt: savedEntry.createdAt,
              fileContent: newContent,
              fileKey: keyToSave,
            }
          : row,
      );

      setRows(updatedRows);
      setOptimisticRows(updatedRows);
      setIsWaitingForBotpressRefresh(true);

      onUpdateMarkdownFile({
        key: keyToSave,
        content: newContent,
        tags: { ...(selectedEntry.fileTags || {}), ...FAQ_KNOWLEDGE_BASE_TAGS },
        metadata: selectedEntry.fileMetadata || {},
        index: true,
      });

      setSelectedEntry(null);
      return;
    }

    lines[selectedEntry.lineIndex] = buildMarkdownEntryLine(entry.clarification);

    const newContent = lines.join("\n");

    console.log("Updating FAQ.md test file", {
      key: keyToSave,
      content: newContent,
    });

    const updatedRows = rows.map(row =>
      row.id === selectedEntry.id
        ? {
            ...row,
            topic: entry.topic,
            clarification: entry.clarification,
            createdAt: entry.createdAt || selectedEntry.createdAt,
            fileContent: newContent,
            fileKey: keyToSave,
          }
        : row,
    );

    setRows(updatedRows);
    setOptimisticRows(updatedRows);
    setIsWaitingForBotpressRefresh(true);

    onUpdateMarkdownFile({
      key: keyToSave,
      content: newContent,
      tags: { ...(selectedEntry.fileTags || {}), ...FAQ_KNOWLEDGE_BASE_TAGS },
      metadata: selectedEntry.fileMetadata || {},
      index: true,
    });

    setSelectedEntry(null);
  };

  const onDeleteMemberVerifiedAnswer = entry => {
    const targetKey = entry.fileKey || entry.sourceFile;

    if (!isSafeFaqFile(targetKey)) {
      console.warn("Blocked delete for non-test KB file", targetKey);
      return;
    }

    setSelectedEntry(null);
    setSelectedDeleteEntry(entry);
  };

  const onAcceptDeleteEntry = () => {
    if (!selectedDeleteEntry || !onUpdateMarkdownFile) {
      setSelectedDeleteEntry(null);
      return;
    }

    const keyToSave = FAQ_MARKDOWN_FILE_KEY;

    const newContent = removeMarkdownEntryBlock(
      selectedDeleteEntry.fileContent || "",
      selectedDeleteEntry.lineIndex,
    );

    console.log("Deleting from FAQ.md test file", {
      key: keyToSave,
      content: newContent,
    });

    const updatedRows = rows.filter(row => row.id !== selectedDeleteEntry.id);

    setRows(updatedRows);
    setOptimisticRows(updatedRows);
    setIsWaitingForBotpressRefresh(true);

    onUpdateMarkdownFile({
      key: keyToSave,
      content: newContent,
      tags: { ...(selectedDeleteEntry.fileTags || {}), ...FAQ_KNOWLEDGE_BASE_TAGS },
      metadata: selectedDeleteEntry.fileMetadata || {},
      index: true,
    });

    setSelectedDeleteEntry(null);
  };

  const onRefreshMemberVerifiedAnswers = () => {
    if (onRefreshTable) {
      onRefreshTable();
      return;
    }

    setRows(parsedRows);
  };

  return (
    <Widget widgetSize={widgetSize} minHeight={minHeight}>
      <WidgetTitle
        title={"Frequently Asked Questions"}
        text={rows != null ? `${rows.length} User Addendums` : "User Addendums"}
      >
        {!readOnly && (
          <OmniResponsiveButton
            icon={<Add />}
            tooltipTitle="Create a new frequently asked question"
            buttonText="Entry"
            onClick={onCreateMemberVerifiedAnswer}
          />
        )}
      </WidgetTitle>

      <WidgetSurveyContent>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          <BotpressMemberVerifiedAnswersTable
            data={rows}
            onRefreshTable={onRefreshMemberVerifiedAnswers}
            onEditEntry={readOnly ? null : onEditMemberVerifiedAnswer}
            onDeleteEntry={readOnly ? null : onDeleteMemberVerifiedAnswer}
          />
        </Box>
      </WidgetSurveyContent>

      {/* Skeleton overlay while the knowledge-base files load. */}
      {knowledgeBaseMarkdownFilesLoading && (
        <Box sx={{ position: "absolute", top: "56px", left: 0, right: 0, bottom: 0, zIndex: 2 }}>
          <WidgetSkeleton />
        </Box>
      )}

      {selectedEntry != null && (
        <BotpressMemberVerifiedAnswersDialog
          open={selectedEntry != null}
          entryRecord={selectedEntry}
          dialogTitle={
            selectedEntry.id == null
              ? "Create Frequently Asked Question"
              : "Edit Frequently Asked Question"
          }
          okMessage={selectedEntry.id == null ? "Create" : "Ok"}
          onAccept={onAcceptMemberVerifiedAnswer}
          onDelete={onDeleteMemberVerifiedAnswer}
          onClose={() => {
            setSelectedEntry(null);
          }}
        />
      )}

      {selectedDeleteEntry != null && (
        <AlertDialog
          open={selectedDeleteEntry != null}
          dialogTitle={"Confirm"}
          dialogMessage={"Are you sure you want to delete this Frequently Asked Question?"}
          onAccept={onAcceptDeleteEntry}
          onClose={() => {
            setSelectedDeleteEntry(null);
          }}
        />
      )}
    </Widget>
  );
};

// The widget is rendered in two places:
//   - Admin portal: read-only, bound to the PROD bot (botpressBotODM).
//   - Test harness portal: read/write, bound to the TEST bot (testBotODM).
// The `variant` prop selects which redux slice / actions back the widget.
const mapStateToProps = (state, ownProps) => {
  if (ownProps.variant === "test") {
    return {
      knowledgeBaseFiles: state.botpressSession.testKnowledgeBaseMarkdownFiles || EMPTY_FILES,
      knowledgeBaseMarkdownFilesLoading:
        state.botpressSession.testKnowledgeBaseMarkdownFilesLoading,
    };
  }

  return {
    knowledgeBaseFiles: state.botpressSession.knowledgeBaseMarkdownFiles || EMPTY_FILES,
    knowledgeBaseMarkdownFilesLoading: state.botpressSession.knowledgeBaseMarkdownFilesLoading,
  };
};

const mapDispatchToProps = (dispatch, ownProps) => {
  if (ownProps.variant === "test") {
    return {
      onRefreshTable: () =>
        dispatch(BotpressSessionActions.loadTestKnowledgeBaseMarkdownFilesRequest({})),

      onUpdateMarkdownFile: markdownFileRecord =>
        dispatch(
          BotpressSessionActions.updateTestKnowledgeBaseMarkdownFileRequest(markdownFileRecord),
        ),
    };
  }

  return {
    onRefreshTable: () =>
      dispatch(BotpressSessionActions.loadKnowledgeBaseMarkdownFilesRequest({})),

    onUpdateMarkdownFile: markdownFileRecord =>
      dispatch(BotpressSessionActions.updateKnowledgeBaseMarkdownFileRequest(markdownFileRecord)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(BotpressMemberVerifiedAnswers);
